// IBS Spark Theme Configuration
// This file contains all theme variables that can be used throughout the application

export const theme = {
  // Primary Colors
  colors: {
    primary: '#243A73',
    primaryLight: '#1e2f5a',
    primaryDark: '#1a2a4a',
    secondary: '#007bff',
    secondaryLight: '#0056b3',
    secondaryDark: '#004085',
    
    // Success Colors
    success: '#28a745',
    successLight: '#20c997',
    successDark: '#1e7e34',
    
    // Warning Colors
    warning: '#ffc107',
    warningLight: '#ffca2c',
    warningDark: '#e0a800',
    
    // Danger Colors
    danger: '#dc3545',
    dangerLight: '#e74c3c',
    dangerDark: '#c82333',
    
    // Info Colors
    info: '#17a2b8',
    infoLight: '#20c997',
    infoDark: '#138496',
    
    // Neutral Colors
    white: '#ffffff',
    light: '#f8f9fa',
    lightGray: '#e9ecef',
    gray: '#6c757d',
    darkGray: '#495057',
    dark: '#343a40',
    black: '#000000',
    
    // Text Colors
    textPrimary: '#333333',
    textSecondary: '#6c757d',
    textMuted: '#9ca3af',
    textLight: '#ffffff',
    brandTextLink: 'rgba(11, 92, 171, 1)',
    
    // Border Colors
    borderLight: '#dee2e6',
    borderMedium: '#ced4da',
    borderDark: '#adb5bd',
    
    // Background Colors
    backgroundPrimary: '#ffffff',
    backgroundSecondary: '#f8f9fa',
    backgroundTertiary: '#e9ecef',
    
    // Gradient Colors
    gradientPrimary: 'linear-gradient(135deg, #243A73 0%, #1e2f5a 100%)',
    gradientSecondary: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
    gradientSuccess: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
    gradientWarning: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
    gradientDanger: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
  },

  // Typography
  typography: {
    fontFamily: {
      primary: '"Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", Arial, sans-serif',
      secondary: '"SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
      mono: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Spacing
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
  },

  // Border Radius
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    md: '0.375rem',  // 6px
    lg: '0.5rem',    // 8px
    xl: '0.75rem',   // 12px
    '2xl': '1rem',   // 16px
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  // Transitions
  transitions: {
    fast: '0.15s ease-in-out',
    normal: '0.2s ease-in-out',
    slow: '0.3s ease-in-out',
  },

  // Z-Index
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },

  // Breakpoints
  breakpoints: {
    xs: '0px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    xxl: '1400px',
  },
};

// Component-specific theme configurations
export const componentTheme = {
  // Button Themes
  button: {
    primary: {
      background: theme.colors.primary,
      color: theme.colors.white,
      border: `1px solid ${theme.colors.primary}`,
      hover: {
        background: theme.colors.primaryDark,
        border: `1px solid ${theme.colors.primaryDark}`,
      },
    },
    secondary: {
      background: theme.colors.secondary,
      color: theme.colors.white,
      border: `1px solid ${theme.colors.secondary}`,
      hover: {
        background: theme.colors.secondaryDark,
        border: `1px solid ${theme.colors.secondaryDark}`,
      },
    },
    outline: {
      background: 'transparent',
      color: theme.colors.primary,
      border: `1px solid ${theme.colors.primary}`,
      hover: {
        background: theme.colors.primary,
        color: theme.colors.white,
      },
    },
    outlineSecondary: {
      background: 'transparent',
      color: theme.colors.secondary,
      border: `1px solid ${theme.colors.secondary}`,
      hover: {
        background: theme.colors.secondary,
        color: theme.colors.white,
      },
    },
    success: {
      background: theme.colors.success,
      color: theme.colors.white,
      border: `1px solid ${theme.colors.success}`,
      hover: {
        background: theme.colors.successDark,
        border: `1px solid ${theme.colors.successDark}`,
      },
    },
    danger: {
      background: theme.colors.danger,
      color: theme.colors.white,
      border: `1px solid ${theme.colors.danger}`,
      hover: {
        background: theme.colors.dangerDark,
        border: `1px solid ${theme.colors.dangerDark}`,
      },
    },
  },

  // Modal Themes
  modal: {
    header: {
      background: theme.colors.gradientPrimary,
      color: theme.colors.white,
      borderBottom: `1px solid ${theme.colors.primaryDark}`,
    },
    body: {
      background: theme.colors.backgroundSecondary,
      color: theme.colors.textPrimary,
    },
    footer: {
      background: theme.colors.backgroundPrimary,
      borderTop: `1px solid ${theme.colors.borderLight}`,
    },
  },

  // Card Themes
  card: {
    header: {
      background: theme.colors.backgroundSecondary,
      color: theme.colors.textPrimary,
      borderBottom: `1px solid ${theme.colors.borderLight}`,
    },
    body: {
      background: theme.colors.backgroundPrimary,
      color: theme.colors.textPrimary,
    },
  },

  // Form Themes
  form: {
    input: {
      background: theme.colors.backgroundPrimary,
      border: `1px solid ${theme.colors.borderMedium}`,
      color: theme.colors.textPrimary,
      focus: {
        border: `1px solid ${theme.colors.primary}`,
        boxShadow: `0 0 0 0.2rem rgba(36, 58, 115, 0.25)`,
      },
    },
    label: {
      color: theme.colors.textPrimary,
      fontWeight: theme.typography.fontWeight.medium,
    },
  },

  // Table Themes
  table: {
    header: {
      background: theme.colors.backgroundSecondary,
      color: theme.colors.textPrimary,
      borderBottom: `1px solid ${theme.colors.borderLight}`,
    },
    row: {
      background: theme.colors.backgroundPrimary,
      hover: {
        background: theme.colors.light,
      },
    },
  },
};

// Utility functions for theme usage
export const getThemeColor = (colorKey) => {
  return theme.colors[colorKey] || colorKey;
};

export const getComponentStyle = (component, variant) => {
  return componentTheme[component]?.[variant] || {};
};

export const createStyleObject = (styles) => {
  return Object.entries(styles).reduce((acc, [key, value]) => {
    if (typeof value === 'object' && value !== null) {
      acc[key] = createStyleObject(value);
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
};

// CSS-in-JS helper
export const css = (styles) => {
  return createStyleObject(styles);
};

export default theme;
